<template>
  <div class="p-6">
    <!-- Header Premium -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
      <div>
        <div class="flex items-center gap-3 mb-1">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-none">
            <Icon icon="mdi:shield-key" class="text-white text-xl" />
          </div>
          <h1 class="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Gestión de Permisos</h1>
        </div>
        <p class="text-sm font-medium text-gray-500 dark:text-gray-400 ml-13">Administre la matriz de facultades y accesos por módulo</p>
      </div>

      <div class="flex items-center gap-3 w-full md:w-auto">
        <div class="relative group flex-1 md:flex-none">
          <button
            @click="showExportMenu = !showExportMenu"
            class="w-full md:w-auto flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm hover:border-blue-500 hover:text-blue-600 transition-all shadow-sm"
          >
            <Icon icon="mdi:printer" class="mr-2 text-lg" />
            Imprimir
            <Icon icon="mdi:chevron-down" class="ml-2 opacity-50" />
          </button>

          <transition enter-active-class="transition duration-200 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-150 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <div v-if="showExportMenu"
                 class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-[60] overflow-hidden">
              <div class="p-2">
                <button @click="printFullReport" class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl flex items-center transition-all group">
                  <div class="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:file-pdf-box" class="text-red-600 text-xl" />
                  </div>
                  <div>
                    <p class="font-black text-[10px] uppercase tracking-wider text-gray-400">PDF COMPLETO</p>
                    <p class="text-sm font-bold text-gray-700 dark:text-gray-300">Resumen Ejecutivo</p>
                  </div>
                </button>
                <div class="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>
                <button @click="printSimplifiedReport" class="w-full px-4 py-3 text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-xl flex items-center transition-all group">
                  <div class="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:table" class="text-indigo-600 text-xl" />
                  </div>
                  <div>
                    <p class="font-black text-[10px] uppercase tracking-wider text-gray-400">TABLA</p>
                    <p class="text-sm font-bold text-gray-700 dark:text-gray-300">Listado de Acciones</p>
                  </div>
                </button>
              </div>
            </div>
          </transition>
        </div>

        <button
          @click="openCreateModal"
          class="flex-1 md:flex-none flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-sm hover:shadow-lg hover:shadow-blue-200 dark:hover:shadow-none hover:translate-y-[-1px] transition-all"
        >
          <Icon icon="mdi:plus" class="mr-2 text-xl" />
          NUEVO PERMISO
        </button>
      </div>
    </div>

    <!-- Panel de Filtros Premium -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl shadow-gray-100 dark:shadow-none p-6 mb-8 border border-gray-100 dark:border-gray-700 relative overflow-hidden">
      <div class="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div class="md:col-span-12 lg:col-span-5">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Buscador de Facultades</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-blue-500 transition-colors" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Nombre, acción o módulo..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-all"
              @input="handleSearch"
            />
          </div>
        </div>

        <div class="md:col-span-4 lg:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Módulo</label>
          <select
            v-model="filterModulo"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold appearance-none cursor-pointer"
            @change="handleFilterChange"
          >
            <option value="all">Todos</option>
            <option v-for="modulo in modulosDisponibles" :key="modulo.idModulo" :value="modulo.idModulo">
              {{ modulo.nombre }}
            </option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-3">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Prioridad Orden</label>
          <select
            v-model="sortBy"
            class="w-full px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold appearance-none cursor-pointer"
          >
            <option value="nombre_accion">Acción (A-Z)</option>
            <option value="modulo">Módulo (Vertical)</option>
            <option value="fechaCreacion_desc">Recientes</option>
            <option value="cantidadRoles">Relevancia (Roles)</option>
          </select>
        </div>

        <div class="md:col-span-4 lg:col-span-2 flex items-end">
          <button
            @click="loadPermisos"
            class="w-full h-[44px] flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-xl hover:bg-blue-500 hover:text-white transition-all group"
            title="Sincronizar"
          >
            <Icon icon="mdi:refresh" class="text-xl group-hover:rotate-180 transition-transform duration-500" :class="{ 'animate-spin': loading }" />
            <span class="ml-2 font-bold text-xs uppercase">Sincronizar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas Premium -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Icon icon="mdi:lock-open-check" class="text-blue-600 dark:text-blue-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Matriz</p>
            <h3 class="text-2xl font-black text-gray-900 dark:text-white">{{ permisos.length }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
            <Icon icon="mdi:view-module" class="text-purple-600 dark:text-purple-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Módulos</p>
            <h3 class="text-2xl font-black text-gray-900 dark:text-white">{{ estadisticas.modulosUnicos }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <Icon icon="mdi:account-group" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Asignaciones</p>
            <h3 class="text-2xl font-black text-emerald-600 dark:text-emerald-400">{{ estadisticas.totalAsignaciones }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Icon icon="mdi:chart-scatter-plot" class="text-indigo-600 dark:text-indigo-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Densidad</p>
            <h3 class="text-2xl font-black text-indigo-600 dark:text-indigo-400">{{ estadisticas.promedioPorModulo }}<small class="text-xs ml-1 font-bold opacity-50">avg</small></h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterModulo !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
          Módulo: {{ getModuloNombre(filterModulo) }}
          <button @click="filterModulo = 'all'; handleFilterChange()" class="ml-1.5 hover:text-purple-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de permisos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Lista de Permisos</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredPermisos.length }} de {{ permisos.length }} permisos
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Permiso / Acción</span>
                  <button @click="sortBy = sortBy === 'nombre_accion' ? 'nombre_accion_desc' : 'nombre_accion'">
                    <Icon icon="mdi:sort-alphabetical" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Módulo
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Descripción
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Roles Asignados
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Fecha Creación
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="loading">
              <td colspan="6" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando permisos...</p>
                </div>
              </td>
            </tr>

            <tr v-else-if="filteredPermisos.length === 0">
              <td colspan="6" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:lock-off" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron permisos
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterModulo !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.'
                      : 'No hay permisos registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterModulo === 'all'"
                    @click="openCreateModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer permiso
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <tr
              v-else
              v-for="permiso in paginatedPermisos"
              :key="permiso.idPermiso"
              class="group hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-all duration-300 border-b border-gray-100 dark:border-gray-700/50 last:border-none"
              @click="openDetailModal(permiso)"
            >
              <td class="px-6 py-5">
                <div class="flex items-center gap-4">
                  <div class="relative">
                    <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/20 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-sm border border-blue-100/50 dark:border-blue-900/30">
                      <Icon
                        :icon="obtenerIconoPorAccion(permiso.nombre_accion)"
                        class="text-2xl text-blue-600 dark:text-blue-400"
                      />
                    </div>
                    <div class="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm animate-pulse"></div>
                  </div>
                  <div>
                    <div class="text-sm font-black text-gray-900 dark:text-white tracking-tight group-hover:text-blue-600 transition-colors">
                      {{ formatearNombreAccion(permiso.nombre_accion) }}
                    </div>
                    <div class="text-[10px] font-bold text-gray-400 dark:text-gray-500 flex items-center mt-0.5 uppercase tracking-tighter">
                      <Icon icon="mdi:identifier" class="mr-1 opacity-50" />
                      ID: {{ permiso.idPermiso }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-5">
                <div class="inline-flex items-center px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
                  <Icon icon="mdi:view-module" class="mr-2 text-xs text-gray-400" />
                  <span class="text-[11px] font-black text-gray-600 dark:text-gray-300 uppercase tracking-tighter">
                    {{ permiso.modulo_nombre || permiso.nombreModulo || 'SISTEMA' }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-5">
                <p class="text-[13px] font-medium text-gray-500 dark:text-gray-400 line-clamp-1 max-w-[200px]">
                  {{ permiso.descripcion || 'Sin descripción detallada' }}
                </p>
              </td>
              <td class="px-6 py-5">
                <div class="flex items-center gap-2">
                  <div class="flex -space-x-2">
                    <div
                      v-for="n in Math.max(0, Math.min(Number(permiso.cantidadRoles) || 0, 3))"
                      :key="n"
                      class="w-7 h-7 rounded-lg border-2 border-white dark:border-gray-800 bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center overflow-hidden">
                      <Icon icon="mdi:account-shield" class="text-xs text-indigo-500" />
                    </div>
                    <div v-if="(Number(permiso.cantidadRoles) || 0) > 3"
                         class="w-7 h-7 rounded-lg border-2 border-white dark:border-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-black text-gray-500">
                      +{{ (Number(permiso.cantidadRoles) || 0) - 3 }}
                    </div>
                  </div>
                  <span v-if="(Number(permiso.cantidadRoles) || 0) === 0" class="text-[10px] font-black text-gray-400 uppercase tracking-tighter italic">No asignado</span>
                </div>
              </td>
              <td class="px-6 py-5 whitespace-nowrap">
                <div class="flex flex-col">
                  <span class="text-[13px] font-bold text-gray-700 dark:text-gray-300">
                    {{ formatDate(permiso.fecha_creacion || permiso.fechaCreacion) }}
                  </span>
                  <span class="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    {{ formatTime(permiso.fecha_creacion || permiso.fechaCreacion) }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-5 whitespace-nowrap text-right">
                <div class="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    @click.stop="openEditModal(permiso)"
                    class="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                    title="Editar Facultad"
                  >
                    <Icon icon="mdi:pencil-outline" class="text-xl" />
                  </button>
                  <button
                    @click.stop="openAssignRolesModal(permiso)"
                    class="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                    title="Asignar Roles"
                  >
                    <Icon icon="mdi:account-lock-outline" class="text-xl" />
                  </button>
                  <button
                    @click.stop="confirmDelete(permiso)"
                    class="p-2.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm"
                    title="Eliminar Permiso"
                  >
                    <Icon icon="mdi:delete-outline" class="text-xl" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredPermisos.length > 0 && !loading" class="px-6 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="text-sm font-medium text-gray-500 dark:text-gray-400">
            Mostrando <span class="text-gray-900 dark:text-white">{{ startItem }}-{{ endItem }}</span> de
            <span class="text-gray-900 dark:text-white">{{ filteredPermisos.length }}</span> resultados
          </div>

          <div class="flex items-center space-x-1">
            <button
              @click="currentPage = Math.max(currentPage - 1, 1)"
              :disabled="currentPage === 1"
              class="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <Icon icon="mdi:chevron-left" class="text-xl" />
            </button>

            <div class="flex items-center space-x-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  'w-10 h-10 flex items-center justify-center rounded-xl font-bold text-sm transition-all shadow-sm',
                  currentPage === page
                    ? 'bg-blue-600 text-white shadow-blue-200 dark:shadow-none translate-y-[-2px]'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-gray-700'
                ]"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="currentPage = Math.min(currentPage + 1, totalPages)"
              :disabled="currentPage === totalPages"
              class="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <Icon icon="mdi:chevron-right" class="text-xl" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL CREAR/EDITAR -->
    <!-- ============================================ -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all tracking-tight">
            <div class="px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-indigo-600">
              <div class="flex justify-between items-center text-white">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-white/20 rounded-xl">
                    <Icon :icon="editingPermiso ? 'mdi:pencil-box' : 'mdi:shield-plus'" class="text-2xl" />
                  </div>
                  <div>
                    <h3 class="text-xl font-black">{{ editingPermiso ? 'Editar' : 'Nueva' }} Facultad</h3>
                    <p class="text-xs text-blue-100 font-medium italic">Configuración de seguridad y accesos</p>
                  </div>
                </div>
                <button @click="closeModal" class="p-2 hover:bg-white/20 rounded-xl transition-colors">
                  <Icon icon="mdi:close" class="text-2xl" />
                </button>
              </div>
            </div>

            <form @submit.prevent="savePermiso" class="p-6 space-y-6">
              <div class="space-y-4">
                <div>
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Módulo Asociado *</label>
                  <select
                    v-model.number="form.idModulo"
                    required
                    class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-bold appearance-none cursor-pointer"
                    :class="{ 'ring-2 ring-red-500': errors.idModulo }"
                  >
                    <option value="">Seleccione un módulo estratégico</option>
                    <option v-for="modulo in modulosDisponibles" :key="modulo.idModulo" :value="modulo.idModulo">
                      {{ modulo.nombre }}
                    </option>
                  </select>
                  <p v-if="errors.idModulo" class="text-[10px] text-red-500 font-bold mt-1 ml-1 flex items-center gap-1">
                    <Icon icon="mdi:alert-circle" class="text-sm" />
                    {{ errors.idModulo }}
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Tipo de Acción *</label>
                    <select
                      v-model="form.nombre_accion"
                      required
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-black appearance-none"
                      :class="{ 'ring-2 ring-red-500': errors.nombre_accion }"
                    >
                      <option value="">Acción estándar</option>
                      <option v-for="accion in accionesComunes" :key="accion" :value="accion">
                        {{ formatearNombreAccion(accion) }}
                      </option>
                      <option value="personalizado">Personalizado...</option>
                    </select>
                  </div>
                  <div v-if="form.nombre_accion === 'personalizado'">
                    <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Codificación *</label>
                    <input
                      v-model="form.nombre_accion_personalizado"
                      type="text"
                      required
                      placeholder="ej: borrar_todo"
                      class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-mono font-bold"
                      :class="{ 'ring-2 ring-red-500': errors.nombre_accion }"
                    />
                  </div>
                </div>

                <!-- Error de acción -->
                <p v-if="errors.nombre_accion" class="text-[10px] text-red-500 font-bold -mt-2 ml-1 flex items-center gap-1">
                  <Icon icon="mdi:alert-circle" class="text-sm" />
                  {{ errors.nombre_accion }}
                </p>

                <!-- Vista previa -->
                <div v-if="finalAccion" class="p-3 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-800/30 flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <Icon :icon="obtenerIconoPorAccion(finalAccion)" class="text-blue-600 dark:text-blue-400 text-xl" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-[9px] font-black text-blue-400 uppercase tracking-widest">Vista previa (se guardará así)</p>
                    <p class="text-xs font-mono font-bold text-blue-700 dark:text-blue-300 truncate">
                      {{ finalAccion }}
                    </p>
                  </div>
                </div>

                <div>
                  <label class="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Alcance y Descripción</label>
                  <textarea
                    v-model="form.descripcion"
                    rows="3"
                    maxlength="500"
                    placeholder="Detalle las capacidades que otorga este permiso..."
                    class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm resize-none font-medium text-gray-600 dark:text-gray-400"
                  ></textarea>
                  <p class="text-[9px] text-gray-400 font-bold mt-1 ml-1 text-right">
                    {{ (form.descripcion || '').length }} / 500
                  </p>
                </div>
              </div>

              <div class="flex gap-3 pt-2">
                <button
                  type="button"
                  @click="closeModal"
                  class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-bold text-sm hover:bg-gray-50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  :disabled="saving || !isFormValid"
                  class="flex-[2] px-4 py-3 bg-blue-600 text-white rounded-xl font-black text-sm hover:bg-blue-700 shadow-lg shadow-blue-200 dark:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Icon v-if="saving" icon="mdi:loading" class="animate-spin text-xl" />
                  <Icon v-else :icon="editingPermiso ? 'mdi:check-bold' : 'mdi:plus-thick'" class="text-xl" />
                  {{ saving ? 'PROCESANDO...' : (editingPermiso ? 'ACTUALIZAR DATOS' : 'GENERAR PERMISO') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal Asignación de Roles -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showAssignRolesModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden transform transition-all flex flex-col max-h-[90vh]">
            <div class="px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-emerald-600 to-teal-600">
              <div class="flex justify-between items-center text-white">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-white/20 rounded-xl">
                    <Icon icon="mdi:account-lock" class="text-2xl" />
                  </div>
                  <div>
                    <h3 class="text-xl font-black">Asignación de Roles</h3>
                    <p class="text-xs text-emerald-100 font-medium italic">Matriz de privilegios para: {{ selectedPermiso?.nombre_accion }}</p>
                  </div>
                </div>
                <button @click="closeAssignRolesModal" class="p-2 hover:bg-white/20 rounded-xl transition-colors">
                  <Icon icon="mdi:close" class="text-2xl" />
                </button>
              </div>
            </div>

            <div class="p-6 overflow-y-auto custom-scrollbar flex-1">
              <div v-if="loadingRoles" class="flex flex-col items-center py-10">
                <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-600 mb-4"></div>
                <p class="text-gray-500 text-xs font-bold uppercase tracking-widest">Consultando asignaciones...</p>
              </div>

              <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  v-for="rol in rolesDisponibles"
                  :key="rol.idRol"
                  :class="[
                    'p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between group',
                    rolesSeleccionados.includes(rol.idRol)
                      ? 'bg-emerald-50 border-emerald-500/30 ring-2 ring-emerald-500/10 dark:bg-emerald-900/20'
                      : 'bg-white border-gray-100 hover:border-emerald-200 dark:bg-gray-800 dark:border-gray-700'
                  ]"
                  @click="toggleRolSelection(rol.idRol)"
                >
                  <div class="flex items-center gap-3">
                    <div :class="[
                      'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
                      rolesSeleccionados.includes(rol.idRol) ? 'bg-emerald-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    ]">
                      <Icon icon="mdi:account-shield" class="text-xl" />
                    </div>
                    <div>
                      <p class="text-sm font-black text-gray-900 dark:text-white">{{ rol.nombre }}</p>
                      <p class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{{ rol.descripcion ? (rol.descripcion.substring(0, 20) + '...') : 'Sin Notas' }}</p>
                    </div>
                  </div>

                  <div :class="[
                    'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
                    rolesSeleccionados.includes(rol.idRol)
                      ? 'bg-emerald-500 border-emerald-500 text-white scale-110'
                      : 'border-gray-200 group-hover:border-emerald-300 opacity-30'
                  ]">
                    <Icon v-if="rolesSeleccionados.includes(rol.idRol)" icon="mdi:check" class="text-xs" />
                  </div>
                </div>
              </div>
            </div>

            <div class="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex gap-3">
              <button
                @click="closeAssignRolesModal"
                class="flex-1 px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-bold text-sm hover:bg-gray-50 transition-all"
              >
                Descartar
              </button>
              <button
                @click="saveRolesAssignment"
                :disabled="savingAssignment || loadingRoles"
                class="flex-[2] px-4 py-3 bg-emerald-600 text-white rounded-xl font-black text-sm hover:bg-emerald-700 shadow-lg shadow-emerald-200 dark:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Icon v-if="savingAssignment" icon="mdi:loading" class="animate-spin text-xl" />
                <Icon v-else icon="mdi:shield-check" class="text-xl" />
                {{ savingAssignment ? 'ACTUALIZANDO...' : 'CONFIRMAR MATRIZ' }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de Detalles -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showDetailModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
            <div class="relative h-32 bg-gradient-to-br from-blue-600 to-indigo-700 overflow-hidden">
              <div class="absolute inset-0 opacity-10">
                <Icon icon="mdi:shield-outline" class="text-[12rem] -translate-x-10 -translate-y-10 rotate-12" />
              </div>
              <button @click="closeDetailModal" class="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors text-white z-10">
                <Icon icon="mdi:close" class="text-xl" />
              </button>
              <div class="absolute -bottom-10 left-8">
                <div class="w-20 h-20 rounded-2xl bg-white dark:bg-gray-800 flex items-center justify-center shadow-2xl border-4 border-white dark:border-gray-800">
                  <Icon :icon="obtenerIconoPorAccion(selectedPermiso?.nombre_accion)" class="text-4xl text-blue-600" />
                </div>
              </div>
            </div>

            <div v-if="selectedPermiso" class="p-8 pt-14">
              <div class="mb-6">
                <h3 class="text-2xl font-black text-gray-900 dark:text-white leading-tight mb-1">{{ formatearNombreAccion(selectedPermiso.nombre_accion) }}</h3>
                <p class="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em]">{{ selectedPermiso.modulo_nombre || selectedPermiso.nombreModulo }}</p>
              </div>

              <div class="space-y-5">
                <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Alcance Administrativo</p>
                  <p class="text-sm font-medium text-gray-600 dark:text-gray-300 italic">
                    {{ selectedPermiso.descripcion || 'Esta facultad no cuenta con una descripción detallada registrada.' }}
                  </p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Identificador</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white">F-{{ selectedPermiso.idPermiso }}</p>
                  </div>
                  <div @click="cargarRolesDelPermiso(selectedPermiso.idPermiso)" class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 cursor-pointer group hover:bg-blue-100 transition-all">
                    <p class="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Roles activos</p>
                    <p class="text-sm font-black text-blue-600 dark:text-blue-300 group-hover:scale-105 transition-transform">{{ Number(selectedPermiso.cantidadRoles) || 0 }} UNIDADES</p>
                  </div>
                </div>

                <div class="pt-6 flex gap-3">
                  <button
                    @click="openEditModal(selectedPermiso); closeDetailModal()"
                    class="flex-1 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-[1.02] transition-all shadow-lg"
                  >
                    <Icon icon="mdi:pencil" class="inline mr-2" />
                    Editar Regla
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de Roles Específicos -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showRolesModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[100] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
            <div class="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
              <h3 class="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tighter">Entidades con Acceso</h3>
              <button @click="showRolesModal = false" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors">
                <Icon icon="mdi:close" class="text-xl text-gray-400" />
              </button>
            </div>

            <div class="p-6">
              <div v-if="loadingRolesDetalle" class="flex flex-col items-center py-10">
                <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                <p class="text-gray-500 text-xs font-bold uppercase tracking-widest">Consultando unidades...</p>
              </div>

              <div v-else-if="rolesDelPermiso.length === 0" class="text-center py-10">
                <div class="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon icon="mdi:account-off" class="text-3xl text-gray-300" />
                </div>
                <p class="text-gray-500 font-bold text-sm tracking-tight">Sin asignaciones vigentes</p>
              </div>

              <div v-else class="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                <div
                  v-for="rol in rolesDelPermiso"
                  :key="rol.idRol"
                  class="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-700 transition-all hover:translate-x-1"
                >
                  <div class="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <Icon icon="mdi:account-circle" class="text-2xl text-purple-600" />
                  </div>
                  <div>
                    <p class="text-sm font-black text-gray-900 dark:text-white leading-tight">{{ rol.nombre }}</p>
                    <p class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{{ rol.descripcion || 'Perfil base' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de Confirmación -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showConfirmModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[100] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
            <div :class="[
              'px-6 py-6 text-center border-b border-gray-100 dark:border-gray-700',
              confirmConfig.type === 'danger' ? 'bg-red-50 dark:bg-red-900/20' :
              confirmConfig.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20' :
              'bg-blue-50 dark:bg-blue-900/20'
            ]">
              <div :class="[
                'w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 shadow-sm',
                confirmConfig.type === 'danger' ? 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300' :
                confirmConfig.type === 'warning' ? 'bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-300' :
                'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300'
              ]">
                <Icon
                  :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon' :
                        confirmConfig.type === 'warning' ? 'mdi:alert' :
                        'mdi:information'"
                  class="text-3xl"
                />
              </div>
              <h3 class="text-xl font-black text-gray-900 dark:text-white lowercase first-letter:uppercase tracking-tight">{{ confirmConfig.title }}</h3>
            </div>

            <div class="p-8 text-center" v-html="confirmConfig.message"></div>

            <div class="px-8 pb-8 flex gap-3">
              <button
                @click="cancelConfirm"
                class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-700 dark:text-gray-300 font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all"
              >
                {{ confirmConfig.cancelText }}
              </button>
              <button
                @click="executeConfirm"
                :class="[
                  'flex-1 px-4 py-3 rounded-2xl text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg',
                  confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700 shadow-red-200 dark:shadow-none' :
                  confirmConfig.type === 'warning' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-200 dark:shadow-none' :
                  'bg-blue-600 hover:bg-blue-700 shadow-blue-200 dark:shadow-none'
                ]"
              >
                {{ confirmConfig.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";

import {
  obtenerPermisos,
  crearPermiso,
  actualizarPermiso,
  eliminarPermiso,
  asignarPermisoARol,
  quitarPermisoDeRol,
  obtenerRolesConPermiso,
  obtenerAccionesComunes,
  buscarPermisos,
  formatearNombreAccion,
  obtenerIconoPorAccion
} from '@/api/permiso.js';

import { obtenerModulos } from '@/api/modulo.js';
import { obtenerRoles } from '@/api/roles.js';

import {
  imprimirReportePermisos,
  imprimirReporteSimplificado
} from '@/utils/reportePermisosGenerator.js';

// ⭐ Helper global para normalizar acciones
function normalizarAccion(valor) {
  if (!valor) return '';
  return valor
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export default {
  name: 'PermisosList',
  components: { Icon },
  data() {
    return {
      permisos: [],
      modulosDisponibles: [],
      rolesDisponibles: [],
      loading: false,
      saving: false,
      savingAssignment: false,
      loadingRoles: false,
      loadingRolesDetalle: false,
      showModal: false,
      showDetailModal: false,
      showAssignRolesModal: false,
      showRolesModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      confirmConfig: {
        title: 'Confirmar',
        message: '',
        detail: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'info',
        onConfirm: null,
        onCancel: null
      },
      searchQuery: '',
      filterModulo: 'all',
      sortBy: 'nombre_accion',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idPermiso: null,
        idModulo: '',
        nombre_accion: '',
        nombre_accion_personalizado: '',
        descripcion: '',
      },
      errors: {},
      editingPermiso: null,
      selectedPermiso: null,
      selectedPermisoId: null,
      rolesSeleccionados: [],
      rolesDelPermiso: [],
      accionesComunes: ['ver', 'crear', 'editar', 'eliminar', 'exportar', 'importar', 'cambiar_estado', 'asignar'],
      lastUpdate: new Date(),
    };
  },
  computed: {
    filteredPermisos() {
      let filtered = [...this.permisos];

      if (this.searchQuery.trim()) {
        filtered = buscarPermisos(filtered, this.searchQuery);
      }

      if (this.filterModulo !== 'all') {
        filtered = filtered.filter(p => p.idModulo === this.filterModulo);
      }

      if (this.sortBy === 'nombre_accion') {
        filtered.sort((a, b) => (a.nombre_accion || '').localeCompare(b.nombre_accion || ''));
      } else if (this.sortBy === 'nombre_accion_desc') {
        filtered.sort((a, b) => (b.nombre_accion || '').localeCompare(a.nombre_accion || ''));
      } else if (this.sortBy === 'modulo') {
        filtered.sort((a, b) => (a.modulo_nombre || '').localeCompare(b.modulo_nombre || ''));
      } else if (this.sortBy === 'fechaCreacion_desc') {
        filtered.sort((a, b) => new Date(b.fecha_creacion || b.fechaCreacion) - new Date(a.fecha_creacion || a.fechaCreacion));
      } else if (this.sortBy === 'fechaCreacion') {
        filtered.sort((a, b) => new Date(a.fecha_creacion || a.fechaCreacion) - new Date(b.fecha_creacion || b.fechaCreacion));
      } else if (this.sortBy === 'cantidadRoles') {
        filtered.sort((a, b) => (Number(b.cantidadRoles) || 0) - (Number(a.cantidadRoles) || 0));
      }

      return filtered;
    },

    estadisticas() {
      const modulosUnicos = new Set(this.permisos.map(p => p.idModulo));
      const totalAsignaciones = this.permisos.reduce((sum, p) => sum + (Number(p.cantidadRoles) || 0), 0);
      const promedioPorModulo = modulosUnicos.size > 0 ? (this.permisos.length / modulosUnicos.size).toFixed(1) : 0;

      return {
        modulosUnicos: modulosUnicos.size,
        totalAsignaciones: totalAsignaciones,
        promedioPorModulo: promedioPorModulo
      };
    },

    paginatedPermisos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPermisos.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filteredPermisos.length / this.itemsPerPage);
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },

    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredPermisos.length);
    },

    isFormValid() {
      if (!this.form.idModulo) return false;
      const accion = this.form.nombre_accion === 'personalizado'
        ? this.form.nombre_accion_personalizado
        : this.form.nombre_accion;
      return !!(accion && accion.trim());
    },

    isFiltered() {
      return this.searchQuery.trim() || this.filterModulo !== 'all';
    },

    // ⭐ finalAccion SIEMPRE normalizada en minúsculas
    finalAccion() {
      const valor = this.form.nombre_accion === 'personalizado'
        ? this.form.nombre_accion_personalizado
        : this.form.nombre_accion;
      return normalizarAccion(valor);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  async mounted() {
    await Promise.all([
      this.loadPermisos(),
      this.loadModulos(),
      this.loadRoles(),
      this.loadAccionesComunes(),
    ]);
  },
  watch: {
    filterModulo() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; },
    sortBy() { this.currentPage = 1; },

    'form.idModulo'() {
      if (this.errors.idModulo) delete this.errors.idModulo;
    },
    'form.nombre_accion'(nuevo) {
      if (this.errors.nombre_accion) delete this.errors.nombre_accion;
      if (nuevo !== 'personalizado') {
        this.form.nombre_accion_personalizado = '';
      }
    },
    'form.nombre_accion_personalizado'() {
      if (this.errors.nombre_accion) delete this.errors.nombre_accion;
    },
  },
  methods: {
    formatearNombreAccion,
    obtenerIconoPorAccion,

    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },

    cancelConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onCancel) this.confirmConfig.onCancel();
    },

    executeConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onConfirm) this.confirmConfig.onConfirm();
    },

    async loadPermisos() {
      this.loading = true;
      try {
        const response = await obtenerPermisos();

        if (response.success) {
          this.permisos = (response.data || []).map(p => ({
            ...p,
            cantidadRoles: Number(p.cantidadRoles) || 0,
            idModulo: p.idModulo ?? p.id_modulo ?? null,
            nombre_accion: normalizarAccion(p.nombre_accion || p.nombreAccion || ''),
            descripcion: p.descripcion || '',
            modulo_nombre: p.modulo_nombre || p.nombreModulo || p.nombre_modulo || 'SISTEMA',
            fecha_creacion: p.fecha_creacion || p.fechaCreacion || null
          }));

          this.lastUpdate = new Date();
          console.log('✅ Permisos cargados:', this.permisos.length);
        } else {
          throw new Error(response.error || 'Error al cargar permisos');
        }
      } catch (error) {
        console.error('❌ Error al cargar permisos:', error);
        this.toast.error('Error al cargar los permisos: ' + error.message);
      } finally {
        this.loading = false;
      }
    },

    async loadModulos() {
      try {
        const response = await obtenerModulos('activos');
        if (response.success) {
          this.modulosDisponibles = response.data || [];
        }
      } catch (error) {
        console.error('❌ Error cargando módulos:', error);
      }
    },

    async loadRoles() {
      try {
        const response = await obtenerRoles();
        if (response.success) {
          this.rolesDisponibles = response.data || [];
        }
      } catch (error) {
        console.error('❌ Error cargando roles:', error);
      }
    },

    // ⭐ loadAccionesComunes NORMALIZA todo a minúsculas
    async loadAccionesComunes() {
      try {
        const response = await obtenerAccionesComunes();
        if (response.success && Array.isArray(response.data)) {
          this.accionesComunes = response.data
            .map(a => {
              const valor = typeof a === 'string'
                ? a
                : (a?.nombre || a?.nombre_accion || a?.value || '');
              return normalizarAccion(valor);
            })
            .filter(Boolean);

          console.log('✅ Acciones comunes normalizadas:', this.accionesComunes);
        }
      } catch (error) {
        console.error('❌ Error cargando acciones comunes:', error);
      }
    },

    handleFilterChange() {
      this.currentPage = 1;
    },

    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),

    openCreateModal() {
      this.editingPermiso = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
    },

    // ⭐ openEditModal normaliza el nombre_accion del permiso
    openEditModal(permiso) {
      this.editingPermiso = permiso;
      const accionNormalizada = normalizarAccion(permiso.nombre_accion);
      const esAccionComun = this.accionesComunes.includes(accionNormalizada);

      this.form = {
        idPermiso: permiso.idPermiso,
        idModulo: permiso.idModulo,
        nombre_accion: esAccionComun ? accionNormalizada : 'personalizado',
        nombre_accion_personalizado: esAccionComun ? '' : accionNormalizada,
        descripcion: permiso.descripcion || '',
      };
      this.errors = {};
      this.showModal = true;
    },

    openDetailModal(permiso) {
      this.selectedPermiso = permiso;
      this.showDetailModal = true;
    },

    openAssignRolesModal(permiso) {
      this.selectedPermiso = permiso;
      this.selectedPermisoId = permiso.idPermiso;
      this.rolesSeleccionados = [];
      this.loadingRoles = true;
      this.showAssignRolesModal = true;
      this.cargarRolesAsignados();
    },

    async cargarRolesAsignados() {
      try {
        const response = await obtenerRolesConPermiso(this.selectedPermisoId);
        if (response.success) {
          const rolesAsignados = response.data || [];
          this.rolesSeleccionados = rolesAsignados.map(r => r.idRol);
        }
      } catch (error) {
        console.error('❌ Error cargando roles asignados:', error);
      } finally {
        this.loadingRoles = false;
      }
    },

    async cargarRolesDelPermiso(idPermiso) {
      this.loadingRolesDetalle = true;
      try {
        const response = await obtenerRolesConPermiso(idPermiso);
        if (response.success) {
          this.rolesDelPermiso = response.data || [];
          this.showRolesModal = true;
        }
      } catch (error) {
        console.error('❌ Error cargando roles del permiso:', error);
        this.toast.error('Error al cargar los roles asignados');
      } finally {
        this.loadingRolesDetalle = false;
      }
    },

    toggleRolSelection(idRol) {
      const index = this.rolesSeleccionados.indexOf(idRol);
      if (index === -1) {
        this.rolesSeleccionados.push(idRol);
      } else {
        this.rolesSeleccionados.splice(index, 1);
      }
    },

    async saveRolesAssignment() {
      if (!this.selectedPermisoId) return;

      this.savingAssignment = true;
      try {
        const response = await obtenerRolesConPermiso(this.selectedPermisoId);
        const rolesActuales = response.success ? (response.data || []).map(r => r.idRol) : [];

        const rolesQuitar = rolesActuales.filter(r => !this.rolesSeleccionados.includes(r));
        const rolesAgregar = this.rolesSeleccionados.filter(r => !rolesActuales.includes(r));

        await Promise.all([
          ...rolesQuitar.map(idRol =>
            quitarPermisoDeRol({ idPermiso: this.selectedPermisoId, idRol })
          ),
          ...rolesAgregar.map(idRol =>
            asignarPermisoARol({ idPermiso: this.selectedPermisoId, idRol })
          )
        ]);

        const permiso = this.permisos.find(p => p.idPermiso === this.selectedPermisoId);
        if (permiso) permiso.cantidadRoles = this.rolesSeleccionados.length;

        this.toast.success('Asignaciones guardadas correctamente');
        this.closeAssignRolesModal();
      } catch (error) {
        console.error('❌ Error guardando asignaciones:', error);
        this.toast.error('Error al guardar las asignaciones');
      } finally {
        this.savingAssignment = false;
      }
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedPermiso = null;
    },

    closeAssignRolesModal() {
      this.showAssignRolesModal = false;
      this.selectedPermiso = null;
      this.selectedPermisoId = null;
      this.rolesSeleccionados = [];
    },

    resetForm() {
      this.form = {
        idPermiso: null,
        idModulo: '',
        nombre_accion: '',
        nombre_accion_personalizado: '',
        descripcion: '',
      };
      this.editingPermiso = null;
    },

    // ⭐ validateForm normaliza antes de validar con regex
    validateForm() {
      this.errors = {};

      if (!this.form.idModulo) {
        this.errors.idModulo = 'Debes seleccionar un módulo';
      }

      // Obtener valor crudo
      let accion = this.form.nombre_accion === 'personalizado'
        ? (this.form.nombre_accion_personalizado || '').trim()
        : this.form.nombre_accion;

      // ⭐ Normalizar SIEMPRE: minúsculas, espacios → _
      accion = normalizarAccion(accion);

      if (!accion) {
        this.errors.nombre_accion = 'La acción es requerida';
      } else if (accion.length > 100) {
        this.errors.nombre_accion = 'La acción no puede exceder 100 caracteres';
      } else if (!/^[a-z0-9_]+$/.test(accion)) {
        this.errors.nombre_accion = 'Usa solo minúsculas, números y guiones bajos';
      }

      if (this.form.descripcion && this.form.descripcion.length > 500) {
        this.errors.descripcion = 'La descripción no puede exceder 500 caracteres';
      }

      return Object.keys(this.errors).length === 0;
    },

    // ⭐ savePermiso SIN verificarNombrePermiso, con normalización
    async savePermiso() {
      if (!this.validateForm()) {
        this.toast.warning('Revisa los campos marcados');
        return;
      }

      this.saving = true;
      this.errors = {};

      try {
        let accion = this.form.nombre_accion === 'personalizado'
          ? this.form.nombre_accion_personalizado.trim()
          : this.form.nombre_accion;

        // ⭐ Normalizar antes de enviar
        accion = normalizarAccion(accion);

        const permisoData = {
          idModulo: Number(this.form.idModulo),
          nombre_accion: accion,
          descripcion: (this.form.descripcion || '').trim() || ''
        };

        console.log('📤 Enviando permiso:', permisoData);

        const result = this.editingPermiso
          ? await actualizarPermiso(this.editingPermiso.idPermiso, permisoData)
          : await crearPermiso(permisoData);

        if (result?.success) {
          await this.loadPermisos();
          const eraEdicion = !!this.editingPermiso;
          this.closeModal();
          this.toast.success(
            eraEdicion ? 'Permiso actualizado correctamente' : 'Permiso creado correctamente'
          );
          return;
        }

        const errorMsg = result?.error || result?.message || 'Error al guardar el permiso';

        if (/ya existe|duplicad|unique|repetid/i.test(errorMsg)) {
          this.errors.nombre_accion = errorMsg;
        } else if (/m[oó]dulo/i.test(errorMsg)) {
          this.errors.idModulo = errorMsg;
        } else {
          this.toast.error(errorMsg);
        }
      } catch (error) {
        console.error('❌ Error al guardar permiso:', error);
        const msg = error?.response?.data?.error
          || error?.response?.data?.message
          || error?.message
          || 'Error al guardar el permiso';
        this.toast.error(msg);
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(permiso) {
      const rolesCount = Number(permiso.cantidadRoles) || 0;
      let mensaje = `¿Estás seguro de eliminar el permiso <strong>"${this.formatearNombreAccion(permiso.nombre_accion)}"</strong>?`;
      let detalle = 'Esta acción no se puede deshacer.';

      if (rolesCount > 0) {
        detalle = `⚠️ ADVERTENCIA: Este permiso está asignado a ${rolesCount} rol(es). Eliminarlo también eliminará estas asignaciones.`;
      }

      const confirmed = await this.showConfirm({
        title: '¿Eliminar permiso?',
        message: mensaje,
        detail: detalle,
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      });

      if (confirmed) {
        await this.deletePermiso(permiso.idPermiso, permiso.nombre_accion);
      }
    },

    async deletePermiso(id, nombre) {
      try {
        const result = await eliminarPermiso(id);
        if (result.success) {
          await this.loadPermisos();
          this.toast.success(`El permiso "${this.formatearNombreAccion(nombre)}" ha sido eliminado`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar permiso:', error);
        this.toast.error(error.message || 'Error al eliminar el permiso');
      }
    },

    getModuloNombre(idModulo) {
      const modulo = this.modulosDisponibles.find(m => m.idModulo === idModulo);
      return modulo ? modulo.nombre : 'Módulo no encontrado';
    },

    printFullReport() {
      if (this.filteredPermisos.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        const filtros = {
          modulo: this.filterModulo !== 'all' ? this.getModuloNombre(this.filterModulo) : null,
          busqueda: this.searchQuery,
          orden: this.sortBy
        };

        imprimirReportePermisos(
          this.filteredPermisos,
          'REPORTE DE PERMISOS - SISTEMA',
          filtros,
          this.modulosDisponibles
        );

        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },

    printSimplifiedReport() {
      if (this.filteredPermisos.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        imprimirReporteSimplificado(
          this.filteredPermisos,
          'LISTADO DE PERMISOS - SISTEMA'
        );

        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },

    printCurrentView() {
      this.printFullReport();
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterModulo = 'all';
      this.sortBy = 'nombre_accion';
      this.currentPage = 1;
    },

    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return fechaISO;
      }
    },

    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return '';
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in { animation: fadeIn 0.3s ease-out; }

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #555; }

.dark ::-webkit-scrollbar-track { background: #374151; }
.dark ::-webkit-scrollbar-thumb { background: #6b7280; }
.dark ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
</style>